import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap, tap, of, catchError, shareReplay, finalize, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { User, AuthResponse, LoginRequest, RegisterRequest } from '../models/models';
import { CapacitorService } from './capacitor.service';
import { SystemMessageService } from '../../services/system-message.service';
import { ENVIRONMENT_TOKEN } from '../../app.config';

/**
 * Auth Service - Shared between Web and Mobile
 *
 * Mobile Adaptation:
 * - Uses encrypted storage via CapacitorService on mobile
 * - Falls back to localStorage on web
 * - Token injection still works the same way via HTTP interceptor
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl: string;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isNativeApp = false;
  private profileRefresh$: Observable<User | null> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private capacitor: CapacitorService,
    private systemMessages: SystemMessageService,
    @Inject(ENVIRONMENT_TOKEN) private environment: any
  ) {
    this.apiUrl = this.environment.apiUrl;
    this.isNativeApp = this.environment.isNativeApp || false;
    this.loadUserFromStorage();
  }

  private hasLocalStorage(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  register(data: RegisterRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/register/`, data);
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/login/`, credentials).pipe(
      switchMap((response: AuthResponse) => {
        if (response?.error) {
          return throwError(() => ({
            status: response.status ?? 403,
            error: response
          }));
        }
        this.setToken(response.token);
        this.setRefreshToken(response.refreshToken);
        this.currentUserSubject.next(response.user);
        return of(response);
      })
    );
  }

  loginWithGoogle(
    idToken: string,
    options?: {
      accepted_privacy_policy?: boolean;
      accepted_agb?: boolean;
      allow_registration?: boolean;
      promo_code?: string;
      verein_id?: number | null;
      neuer_verein?: {
        name: string;
        kurzname: string;
        ort?: string;
        plz?: string;
        email?: string;
      };
    }
  ): Observable<AuthResponse> {
    const payload: any = { id_token: idToken };
    if (options?.accepted_privacy_policy !== undefined) {
      payload.accepted_privacy_policy = options.accepted_privacy_policy;
    }
    if (options?.accepted_agb !== undefined) {
      payload.accepted_agb = options.accepted_agb;
    }
    if (options?.allow_registration) {
      payload.allow_registration = true;
    }
    if (options?.promo_code) {
      payload.promo_code = options.promo_code;
    }
    if (options?.verein_id !== undefined && options?.verein_id !== null) {
      payload.verein_id = options.verein_id;
    }
    if (options?.neuer_verein) {
      payload.neuer_verein = options.neuer_verein;
    }
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/google-login/`, payload).pipe(
      switchMap((response: AuthResponse) => {
        if (response?.error) {
          return throwError(() => ({
            status: response.status ?? 403,
            error: response
          }));
        }
        this.setToken(response.token);
        this.setRefreshToken(response.refreshToken);
        this.currentUserSubject.next(response.user);
        return of(response);
      })
    );
  }

  logout(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUser();
    this.systemMessages.clearSeen();
    this.currentUserSubject.next(null);
    this.router.navigate(['/welcome']);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // ===== TOKEN STORAGE =====
  // Web: localStorage
  // Mobile: localStorage (Phase 2: encrypted storage)

  getToken(): string | null {
    if (!this.hasLocalStorage()) return null;
    return localStorage.getItem('token');
  }

  setToken(token: string): void {
    if (!this.hasLocalStorage()) return;
    localStorage.setItem('token', token);
  }

  private removeToken(): void {
    if (!this.hasLocalStorage()) return;
    localStorage.removeItem('token');
  }

  setRefreshToken(token: string): void {
    if (!this.hasLocalStorage()) return;
    localStorage.setItem('refreshToken', token);
  }

  private removeRefreshToken(): void {
    if (!this.hasLocalStorage()) return;
    localStorage.removeItem('refreshToken');
  }

  getRefreshToken(): string | null {
    if (!this.hasLocalStorage()) return null;
    return localStorage.getItem('refreshToken');
  }

  // ===== USER STORAGE =====
  // Web: localStorage
  // Mobile: localStorage + offline DB

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  private removeUser(): void {
    // Keine persistierte User-Struktur mehr
  }

  private loadUserFromStorage(): void {
    const token = this.getToken();
    let hasToken = false;

    if (token) {
      const payload = this.decodeTokenPayload(token);
      if (payload?.user_id) {
        this.currentUserSubject.next({
          id: payload.user_id,
          email: payload.email ?? '',
          vorname: payload.vorname ?? '',
          nachname: payload.nachname ?? '',
          role: payload.role,
          preferred_schein: payload.preferred_schein ?? '',
          kurs_id: payload.kurs_id ?? null,
          verein_id: payload.verein_id ?? null
        } as User);
        hasToken = true;
      }
    }

    // Wenn Token vorhanden und online, Profil nachladen (füllt Vorname/Nachname)
    if (hasToken && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
      this.fetchUserFromServer().subscribe();
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Check if user has admin privileges
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'ADMIN' || user?.role === 'TRAINER';
  }

  /**
   * Clear all auth data (for testing or logout)
   */
  clearAllAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    this.currentUserSubject.next(null);
  }

  /**
   * Kompatibilitäts-Helfer für ältere Aufrufer (Login-Screen).
   * Prüft minimal, ob ein Token vorhanden ist.
   */
  async checkOfflineLogin(): Promise<boolean> {
    return !!this.getToken();
  }

  /**
   * Server-trusted Profil laden. Offline liefert null.
   */
  fetchUserFromServer(): Observable<User | null> {
    const token = this.getToken();
    if (!token) {
      this.currentUserSubject.next(null);
      return of(null);
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return of(null);
    }

    if (this.profileRefresh$) {
      return this.profileRefresh$;
    }

    this.profileRefresh$ = this.http.get<User>(`${this.apiUrl}/users/profile.php`).pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
      }),
      catchError((err) => {
        console.warn('[AuthService] Profil-Refresh fehlgeschlagen, behalte aktuellen User', err);
        // Kein Logout, um bestehende Sitzung nicht zu verlieren (UI bleibt eingeloggt)
        return of(this.currentUserSubject.value);
      }),
      finalize(() => {
        this.profileRefresh$ = null;
      }),
      shareReplay(1)
    );

    return this.profileRefresh$;
  }

  private decodeTokenPayload(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload;
    } catch {
      return null;
    }
  }
}
